import * as _ from 'lodash'
import {IBusinessObject} from '../PA.Core/PA.Core.IF.BO';
import {IDateRange, IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {IHaveTags} from '../PA.Core/PA.Core.IF.Aspects';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {
    WB_DistribChannelRow,
    WB_DivisionRow,
    WB_FinishedProductRelationshipRow,
    WB_FinishedProductRow, WB_FinishedProductSalesAreaRow,
    WB_FinishedProductTagRelationshipRow, WB_MasterDataSetRow,
    WB_ProductGroupRow, WB_ProductTypeRow,
    WB_ProductUOMRow, WB_SalesAreaRow, WB_SalesOrgRow, WB_SourceRow,
    WB_TagRow,
    WB_TagSourceRow
} from "../PA.Core/PAdbSchemaRows";
import {IwbFinishedProduct} from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_DistribChannel,
    esWB_Division,
    esWB_Finished_Product,
    esWB_Finished_Product_Relationship, esWB_Finished_Product_SalesArea,
    esWB_Finished_Product_Tag_Relationship, esWB_MasterData_Set, esWB_Product_Attribute,
    esWB_Product_BOM,
    esWB_Product_Group, esWB_Product_Type,
    esWB_Product_UOM, esWB_SalesArea, esWB_SalesOrg, esWB_Source,
    esWB_Tag,
    esWB_Tag_Source
} from "../PA.Core/PAdbSchema";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {ProductBOValidations} from "./ProductBO.Validation";
import {ProductBOAudit} from "./ProductBO.Audit";
import {DateUtils} from "../PA.Core/Basic/Dates";
import {BehaviorSubject} from "rxjs";
import {ASync} from "../PA.Core/Basic/ASync";

import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {IUOMCalculateOptions, ProductUOMUtils} from "../PA.MD.Services/ProductUOMUtils";
import {AuditService} from "../PA.Core/Security/AuditService";
import {IProductCreateLoadOptions, IProductType, ProductHeaderFields} from "../PA.MD.Core/PA.MD.IF.Product";
import {ProductEntity} from "../PA.MD.Entity/ProductEntity";
import {IEntity} from "../PA.MD.Core/PA.MD.IF.Entity";
import {MDUsageService} from "../PA.MD.Services/MDUsageService";

const ProductLoadOptions_default: IProductCreateLoadOptions = {}

export class ProductBO
    implements IBusinessObject,
        IHaveTags {
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_Product: WB_FinishedProductRow;
    dctAuditedDeletes: { [id: number]: number }

    constructor(options?: IProductCreateLoadOptions) {

        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New Product'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_Product = null;
        this.dctAuditedDeletes = {}
        this.IHaveSalesAreas_init()
        this.IHaveTagsLoaded_init()
        this.Entity_init()
    }

    get WB_Product(): WB_FinishedProductRow {
        return this.drWB_Product;
    }


    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: IProductCreateLoadOptions): Promise<IBusinessObject> {
        const self = this;
        self.LoadProgressProvider.start("Creating Product");

        let internalOnComplete = self._OnLoadComplete = (async () => {

            try {

                self.LoadOptions = options || {};
                _.defaults(self.LoadOptions, ProductLoadOptions_default)
                if ('dbContext' in self.LoadOptions)
                    self.dbContext = self.LoadOptions.dbContext;
                else
                    self.dbContext = new PAdbContext();

                if (this.dbContext == undefined) {
                    var sError = "Unable to determine dbContext for new Product.";
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }

                // Create record using dbContext
                let newProductRow: WB_FinishedProductRow = <WB_FinishedProductRow>await self.dbContext.createRecordWithKey(esWB_Finished_Product)

                newProductRow.setDefaults()
                self.drWB_Product = newProductRow;
                self.dbContext.add(esWB_Finished_Product, newProductRow)
                self.Identity.setAll(self.WB_Product.cif.WB_PROD_ID, self.WB_Product.cif.PRODUCT_ID, self.WB_Product.cif.PRODUCT_NAME);
                self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                self.Identity.listenToRecordUpdates(self.WB_Product, 'WB_PROD_ID', 'PRODUCT_ID', 'PRODUCT_NAME')


                self.LoadProgressProvider.finish();
                self._bIsLoaded = true;
                self._OnLoadComplete = undefined;


                await self.OnIHaveTagsLoaded

                // Create Defaults
                await self.applyCreateOptions()
                self.Identity.setAll(self.WB_Product.cif.WB_PROD_ID, self.WB_Product.cif.PRODUCT_ID, self.WB_Product.cif.PRODUCT_NAME);

                await self.IProductType_OnLoadComplete()

                await self.Entity_OnLoadComplete()

                self.LoadProgressProvider.finish();
                return self

            } catch (err) {
                self.LoadProgressProvider.exception(err)
                return self
            }

        })();

        return internalOnComplete
    }

    async applyCreateOptions() {
        const self = this

        let sId = self.CreateOptions?.sId || self.LoadOptions?.sId
        if (!_.isEmpty(sId))
            self.WB_Product.cif.PRODUCT_ID = sId

        let sName = self.CreateOptions?.sName || self.LoadOptions?.sName
        if (!_.isEmpty(sName))
            self.WB_Product.cif.PRODUCT_NAME = sName


        let sKeySetId = self.CreateOptions?.sKeySetId || self.LoadOptions?.sKeySetId
        if (!_.isEmpty(sKeySetId))
            self.WB_Product.cif.PRODUCT_SET_ID = sKeySetId

        // Set Master Data Set
        let wbMasterDataSet = self.CreateOptions?.wbMasterDataSetId || self.LoadOptions?.wbMasterDataSetId
        if (_.isNumber(wbMasterDataSet)) {
            let mds: WB_MasterDataSetRow = <any>await self.dbContext.find(esWB_MasterData_Set, wbMasterDataSet)
            if (mds) {
                self.WB_Product.WB_MasterDataSetRow = mds
                self.WB_Product.cif.WB_MDSET_ID = wbMasterDataSet
                let mruValue = new MDUsageService()
                await mruValue.saveMDSetUsage(wbMasterDataSet)
            }
        }
        // Set Source
        let wbSource = self.CreateOptions?.wbSourceId || self.LoadOptions?.wbSourceId
        if (_.isNumber(wbSource)) {
            let src: WB_SourceRow = <any>await self.dbContext.find(esWB_Source, wbSource)
            if (src) {
                self.WB_Product.cif.SOURCE_ID = src.cif.SOURCE_ID

            }
        }

        // Add SalesArea
        let wbSalesAreaId = self.CreateOptions?.wbSalesAreaId || self.LoadOptions?.wbSalesAreaId
        if (_.isNumber(wbSalesAreaId) && !_.isArray(wbSalesAreaId))
            wbSalesAreaId = [wbSalesAreaId]
        for (let aSalesAreaId of (wbSalesAreaId || [])) {
            let newRec = await self.addSalesArea(aSalesAreaId)
        }

        // Set Product Type
        let wbPrdMId = self.CreateOptions?.wbPrdMId || self.LoadOptions?.wbPrdMId
        if (_.isNumber(wbPrdMId)) {
            let aValue: WB_ProductTypeRow = <any>await self.dbContext.find(esWB_Product_Type, wbPrdMId)
            if (aValue) {
                self.WB_Product.cif.WB_PRDM_ID = aValue.cif.WB_PRDM_ID
            }
        }

    }


    Load(anId: number | number, options?: IProductCreateLoadOptions): Promise<IBusinessObject> {
        const self = this;

        let recordResolver: ESRecordResolver
        let recordResolverLoadPromise: Promise<void>

        self.Identity.setAll(anId, anId, 'Loading Product (' + anId + ')')

        let internalOnComplete = self._OnLoadComplete = (async () => {

            try {
                self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");

                if (self.bIsLoaded) {
                    let sError = "Product already loaded.";
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }

                self.LoadOptions = options || {};

                if ('dbContext' in self.LoadOptions)
                    self.dbContext = self.LoadOptions.dbContext;
                else
                    self.dbContext = new PAdbContext();

                if (_.isEmpty(self.dbContext)) {
                    const sError = "Unable to determine dbContext for new Product.";
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }

                if ('RecordResolver' in self.LoadOptions) {
                    recordResolver = (<any>self.LoadOptions).RecordResolver
                    recordResolverLoadPromise = ASync.resolve()
                } else {
                    recordResolver = ProductBO.createRecordResolver({dbContext: self.dbContext})
                    recordResolverLoadPromise = ProductBO.setupRecordResolver(recordResolver, [<number>anId])
                }

                await recordResolverLoadPromise

                let aProductRow: any = recordResolver.get(esWB_Finished_Product, anId)

                if (_.isEmpty(aProductRow)) {
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception("Id not found.");
                    return self
                }

                self.drWB_Product = aProductRow;
                self.Identity.setAll(self.WB_Product.cif.WB_PROD_ID, self.WB_Product.cif.PRODUCT_ID, self.WB_Product.cif.PRODUCT_NAME);
                self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                self.Identity.listenToRecordUpdates(self.WB_Product, 'WB_PROD_ID', 'PRODUCT_ID', 'PRODUCT_NAME')

                let mruValue = new MDUsageService()
                await mruValue.saveMDSetUsage(self.drWB_Product.cif.WB_MDSET_ID)

                self._bIsLoaded = true;
                self._OnLoadComplete = undefined;

                await self.IProductType_OnLoadComplete()
                await self.OnIHaveTagsLoaded
                await self.Entity_OnLoadComplete()

                self.LoadProgressProvider.finish();
                return self;
            } catch (err: any) {
                self._OnLoadComplete = undefined;
                self.LoadProgressProvider.exception(err);
                return self
            }
            ;

        })();


        return internalOnComplete
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }


    async Validate(options?: any): Promise<IBusinessObject> {
        const self = this;

        try {
            self._bIsValidated = false;

            self.ValidateMessageProvider.clearMessages();
            self.ValidateProgressProvider.start("Validating " + self.WB_Product.cif.PRODUCT_NAME + "(" + self.WB_Product.cif.PRODUCT_ID + ")");

            self.ValidateOptions = options || {};

            if (_.isEmpty(self.dbContext)) {
                let sError = "Unable to determine dbContext for to Validate Product.";
                self.ValidateProgressProvider.exception(sError);
                return self
            }

            await ProductBOValidations.ValidateEmptyFields(self)
            if (self.productEntity) {
                await self.productEntity.Validate()
                self.ValidateMessageProvider.addMessages(self.productEntity.ValidateMessageProvider.Messages)
            }

            if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                self._bIsValidated = false;
            } else {
                self._bIsValidated = true;
            }
            self.ValidateProgressProvider.finish();
            return self

        } catch (err) {
            self._bIsValidated = false;
            self.ValidateProgressProvider.exception(err);
            return self
        }

    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }


    Save(options?: { bSkipValidations?: boolean }): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: { bSkipValidations?: boolean }): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    async SaveUsing(bBatch: boolean, options?: { bSkipValidations?: boolean }): Promise<IBusinessObject> {
        const self = this;

        const wbProduct = <IwbFinishedProduct>self.drWB_Product;
        let opts = options || {}

        self.SaveProgressProvider.start("Saving " + wbProduct.PRODUCT_NAME + "(" + wbProduct.PRODUCT_ID + ")");

        try {
            if (_.isEmpty(this.dbContext)) {
                self._bIsSaved = false;
                let sError = "Unable to determine dbContext to Save Product.";
                self.SaveProgressProvider.exception(sError);
                return self
            }
            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }

            if (opts.bSkipValidations)
                self._bIsValidated = true
            else {
                await self.Validate({})

                if (!self.bIsValidated) {
                    self._bIsSaved = false;
                    self.SaveProgressProvider.finish();
                    return self
                }
            }


            let auditService = await ProductBOAudit.ProductAuditBO(self)

            let tasks: Promise<any>[] = []
            tasks.push(saveFunction(esWB_Finished_Product, self.drWB_Product, {}))
            tasks.push(saveFunction(esWB_Finished_Product_SalesArea, self.drWB_Product.WB_FinishedProductSalesAreaRows, {}))
            tasks.push(saveFunction(esWB_Finished_Product_Relationship, self.drWB_Product.WB_FinishedProductRelationshipRows))
            tasks.push(saveFunction(esWB_Finished_Product_Tag_Relationship, self.drWB_Product.WB_FinishedProductTagRelationshipRows))
            tasks.push(saveFunction(esWB_Product_UOM, self.WB_Product.WB_ProductUOMRows))
            tasks.push(saveFunction(esWB_Product_BOM, self.WB_Product.WB_ProductBOMRows))
            tasks.push(saveFunction(esWB_Product_Attribute, self.WB_Product.WB_ProductAttributeRows));

            await Promise.all(tasks)
            if (auditService)
                await auditService.Save(bBatch)


            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                })
            } else {
                self._bIsSaved = true;
            }
            self.SaveProgressProvider.finish();
            return self
        } catch (err) {
            self.SaveProgressProvider.exception(err);
            return self
        }

    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        var self = this;
        var wbProduct = <IwbFinishedProduct>self.drWB_Product;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbProduct.PRODUCT_NAME + "(" + wbProduct.WB_PROD_ID + ")");
        return new Promise<IBusinessObject>((resolve, error) => {

            // 1. Validate if we can delete the Product

            // 2. If Valid, mark all the records for deletion.
            self.drWB_Product.markDeleted()

            _.forEach(self.WB_Product.WB_FinishedProductTagRelationshipRows, (esc: WB_FinishedProductTagRelationshipRow) => {
                if (esc.isActive) {
                    esc.markDeleted()
                }
            })

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);

                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err)
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }


    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    //</editor-fold>

    //<editor-fold desc = "Product Group operations">

    addProductGrpRel(aWbPrdGrpId: number): Promise<IBusinessObject> {
        var self = this

        return new Promise<IBusinessObject>((resolve, error) => {

            // first find out if record exists.
            var found = false

            _.forEach(self.drWB_Product.WB_FinishedProductRelationshipRows, (aRelRow: WB_FinishedProductRelationshipRow) => {
                if (aRelRow.isActive && aRelRow.cif.WB_PRDGRP_ID == aWbPrdGrpId) {
                    found = true
                    return false
                }
            })

            if (found) {
                return
            } // exists..
            var aPrdGrpRow: WB_ProductGroupRow = undefined


            self.dbContext.find(esWB_Product_Group, aWbPrdGrpId)
                .then((aPGRecord: WB_ProductGroupRow) => {
                    if (_.isEmpty(aPGRecord)) {
                        resolve(self)
                        return
                    }
                    aPrdGrpRow = aPGRecord
                    return self.dbContext.find(esWB_Product_Type, aPGRecord.cif.WB_PRDM_ID)
                }).then((typeRow: WB_ProductTypeRow) => {
                aPrdGrpRow.WB_ProductTypeRow = typeRow
                // create new records
                return self.dbContext.createRecordWithKey(esWB_Finished_Product_Relationship)
            })
                .then((aPrdGrpRelRow: WB_FinishedProductRelationshipRow) => {
                    if (_.isEmpty(aPrdGrpRelRow)) {
                        resolve(self)
                        return
                    }
                    aPrdGrpRelRow.setDefaults()
                    aPrdGrpRelRow.cif.SOURCE_ID = self.drWB_Product.cif.SOURCE_ID
                    aPrdGrpRelRow.cif.WB_PROD_ID = self.drWB_Product.cif.WB_PROD_ID
                    aPrdGrpRelRow.WB_FinishedProductRow = self.WB_Product
                    aPrdGrpRelRow.cif.WB_PRDGRP_ID_PRNT = aPrdGrpRow.cif.WB_PRDGRP_ID
                    aPrdGrpRelRow.WB_ProductGroupRow = aPrdGrpRow
                    aPrdGrpRelRow.cif.MANUAL_IND = "1"
                    self.dbContext.add(esWB_Finished_Product_Relationship, aPrdGrpRelRow)
                    resolve(self)
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    removeProductGrpRel(aWbRelId: number): Promise<IBusinessObject> {
        const self = this

        return new Promise<IBusinessObject>((resolve, error) => {

            _.forEach(self.drWB_Product.WB_FinishedProductRelationshipRows, (aRelRow: WB_FinishedProductRelationshipRow) => {
                if (aRelRow.isActive && aRelRow.cif.WB_PRODR_ID == aWbRelId) {
                    aRelRow.markDeleted()
                    aRelRow.cif.DELETED_DT = DateUtils.convertToSAPBWTs(new Date()).toString().substr(0, 8)
                    aRelRow.cif.INTEGRATION_IND = '0'
                }
            })

            resolve(self)
        })
    }

    //</editor-fold>

    //<editor-fold desc = "Sales Area">

    _salesAreasChanged: BehaviorSubject<ProductBO>

    IHaveSalesAreas_init() {
        this._salesAreasChanged = new BehaviorSubject<ProductBO>(this)
    }

    get SalesAreasChanged(): BehaviorSubject<ProductBO> {
        return this._salesAreasChanged
    }

    async addSalesArea(wbSalesAreaId: number): Promise<WB_FinishedProductSalesAreaRow> {
        const self = this

        for (let rel of self.WB_Product.WB_FinishedProductSalesAreaRows) {
            if (!rel.isActive)
                continue
            if (rel.cif.WB_SALESAREA_ID == wbSalesAreaId)
                return rel
        }

        let saRow: WB_SalesAreaRow = await self.dbContext.find(esWB_SalesArea, wbSalesAreaId)
        if (_.isEmpty(saRow))
            return undefined

        if (saRow.cif.WB_SALESORG_ID > 0) {
            let soRow: WB_SalesOrgRow = <any>await self.dbContext.find(esWB_SalesOrg, saRow.cif.WB_SALESORG_ID)
            if (!_.isEmpty(soRow) && _.isEmpty(saRow.WB_SalesOrgRow)) {
                saRow.WB_SalesOrgRow = soRow
            }
        }

        if (saRow.cif.WB_DIVISION_ID > 0) {
            let dvRow: WB_DivisionRow = <any>await self.dbContext.find(esWB_Division, saRow.cif.WB_DIVISION_ID)
            if (!_.isEmpty(dvRow) && _.isEmpty(saRow.WB_DivisionRow)) {
                saRow.WB_DivisionRow = dvRow
            }
        }

        if (saRow.cif.WB_DISTRIBCHANNEL_ID > 0) {
            let dcRow: WB_DistribChannelRow = <any>await self.dbContext.find(esWB_DistribChannel, saRow.cif.WB_DISTRIBCHANNEL_ID)
            if (!_.isEmpty(dcRow) && _.isEmpty(saRow.WB_DistribChannelRow)) {
                saRow.WB_DistribChannelRow = dcRow
            }
        }

        let newRow: WB_FinishedProductSalesAreaRow = <any>await self.dbContext.createRecordWithKey(esWB_Finished_Product_SalesArea)
        newRow.setDefaults()
        newRow.WB_ProductRow = self.WB_Product
        newRow.cif.WB_PROD_ID = self.WB_Product.cif.WB_PROD_ID
        newRow.WB_SalesAreaRow = saRow
        newRow.cif.WB_SALESAREA_ID = wbSalesAreaId

        self.SalesAreasChanged.next(self)
        return newRow
    }

    async removeSalesArea(wbProdSRId: number): Promise<void> {
        const self = this

        for (let rel of self.WB_Product.WB_FinishedProductSalesAreaRows) {
            if (!rel.isActive)
                continue
            if (rel.cif.WB_PRODSR_ID == wbProdSRId) {
                rel.markDeleted()
                break
            }
        }

        self.SalesAreasChanged.next(self)
    }

    //</editor-fold>

    //<editor-fold desc = "IProductType operations">

    private _ProductType: IProductType

    get ProductType(): IProductType {
        return this._ProductType
    }

    async IProductType_OnLoadComplete(): Promise<void> {
        const self = this

        let ptrow = self.drWB_Product?.WB_ProductTypeRow
        let ptr = ptrow?.cif

        let bDefaultTrue = _.isEmpty(ptrow)

        let pt: IProductType = {
            id: ptr?.WB_PRDM_ID || 0,
            id_ui: ptr?.PRDM_ID || 'Not Typed',
            name: ptr?.PRDM_NAME || 'Not Typed',
            bStatusShowInd: (!!ptrow?.bStatusShowInd) || bDefaultTrue,
            bStatusSearchByInd: !!ptrow?.bStatusSearchByInd,
            bSalesAreaShowInd: (!!ptrow?.bSalesAreaShowInd) || bDefaultTrue,
            bSalesAreaSearchByInd: !!ptrow?.bSalesAreaSearchByInd,
            bProductSizesShowInd: (!!ptrow?.bSizesShowInd) || bDefaultTrue,
            bProductSizesSearchByInd: !!ptrow?.bSizesSearchByInd,
            bConsumerUPCShowInd: (!!ptrow?.bConsumerUPCShowInd) || bDefaultTrue,
            bConsumerUPCSearchByInd: !!ptrow?.bConsumerUPCSearchByInd,
            bUOMShowInd: (!!ptrow?.bUOMShowInd) || bDefaultTrue,
            bUOMSearchByInd: !!ptrow?.bUOMSearchByInd,
            bBOMShowInd: (!!ptrow?.bBOMShowInd) || bDefaultTrue,
            bBOMSearchByInd: !!ptrow?.bBOMSearchByInd,
            ProductFieldsShowInd: {},
            SalesAreaFieldsShowInd: {}
        }

        let dctHeaderShowFields = _.keyBy((ptr?.HEADER_SHOW_FIELDS || "").split(","), (a) => a)

        _.forEach(ProductHeaderFields, (a) => {
            pt.ProductFieldsShowInd[a] = (a in dctHeaderShowFields)
        })

        self._ProductType = pt

    }


    //</editor-fold>

    //<editor-fold desc = "Entity - Attributes">

    private productEntity: ProductEntity

    Entity_init(): void {

    }

    async Entity_OnLoadComplete(): Promise<void> {
        this.productEntity = new ProductEntity({productRow: this.drWB_Product})
        await this.productEntity.OnLoadComplete()
    }

    get Entity(): IEntity {
        return this.productEntity
    }

    //</editor-fold>

    //<editor-fold desc = "IHaveTags operations">

    _bIHaveTagsLoaded: boolean;
    _OnIHaveTagsLoaded: Promise<IHaveTags>;
    _TagsChanged: BehaviorSubject<IHaveTags>;
    _AllTags: WB_TagRow[];
    _TagUsageId: string;


    IHaveTagsLoaded_init() {
        this._bIHaveTagsLoaded = false
        this._OnIHaveTagsLoaded = undefined
        this._TagsChanged = new BehaviorSubject<IHaveTags>(this)
        this._AllTags = undefined
        this._TagUsageId = 'AllTagUsage'

    }

    get bIHaveTagsLoaded() {
        return this._bIHaveTagsLoaded
    }

    get TagsReadOnly(): boolean {
        if (this._bIsDeleted) {
            return true
        }
        // Check User Roles ?

        return false
    }

    get Tags(): WB_TagRow[] {
        var returnValue: WB_TagRow[] = []

        _.forEach(this.drWB_Product.WB_FinishedProductTagRelationshipRows, (aWBProdTagRow: WB_FinishedProductTagRelationshipRow) => {
            if (aWBProdTagRow && aWBProdTagRow.isActive && aWBProdTagRow.WB_TagRow) {
                returnValue.push(aWBProdTagRow.WB_TagRow)
            }
        })

        return returnValue
    }

    get TagsChanged(): BehaviorSubject<IHaveTags> {
        return this._TagsChanged
    }

    get AllTags(): WB_TagRow[] {
        if (this._AllTags) {
            return this._AllTags
        } else {
            return []
        }
    }

    get UsageId(): string {
        return this._TagUsageId
    }

    get OnIHaveTagsLoaded(): Promise<IHaveTags> {
        const self = this;

        if (self._bIHaveTagsLoaded) {
            return ASync.resolve(self)
        }

        if (!_.isEmpty(self._OnIHaveTagsLoaded)) {
            return self._OnIHaveTagsLoaded;
        }

        self._OnIHaveTagsLoaded = new Promise<IHaveTags>((resolve, error) => {
            // If the BO is not loaded, throw an error.. It should have been started
            if ((!(self.bIsLoaded)) && (_.isEmpty(self.OnLoadComplete))) {
                error("Product isn't Loaded or Loading.. Can't determine Tags");
                return;
            }

            var loadWait: Promise<any> = undefined

            if (self.bIsLoaded) {
                loadWait = ASync.resolve(self)
            } else {
                loadWait = self.OnLoadComplete
            }

            loadWait
                .then((aProductBO) => {
                    if (!(aProductBO.bIsLoaded)) {
                        error("Waited on Product Load to complete but still isn't Loaded");
                        return
                    }

                    return self.dbContext.findAll(esWB_Tag_Source, {TAG_SOURCE_ID: 'PRODUCT'})
                })
                .then((sourceRows: WB_TagSourceRow[]) => {
                    let source_id = 0
                    if (sourceRows) {
                        source_id = sourceRows[0].cif.WB_TAG_SOURCE_ID
                    }
                    return self.dbContext.findAll(esWB_Tag, {WB_TAG_SOURCE_ID: source_id})
                })
                .then((arrResultRows: WB_TagRow[]) => {
                    if (!_.isEmpty(arrResultRows)) {
                        self._AllTags = arrResultRows
                    } else {
                        self._AllTags = []
                    }

                    self._bIHaveTagsLoaded = true
                    self._OnIHaveTagsLoaded = undefined
                    resolve(self);
                    return
                })
                .catch((err) => {
                    self._OnIHaveTagsLoaded = undefined
                    error(err)
                });
        })
        return self._OnIHaveTagsLoaded;
    }

    addTag(TagId: number): Promise<void> {
        const self = this
        return new Promise<void>((resolve, error) => {
            var aTagRow: WB_TagRow = undefined
            self.dbContext.find(esWB_Tag, TagId)
                .then((aNewRow: WB_TagRow) => {
                    if (!(aNewRow)) {
                        error("Unable to find Tag to add to Event. " + TagId)
                        return
                    }

                    aTagRow = aNewRow

                    return self.dbContext.createRecordWithKey(esWB_Finished_Product_Tag_Relationship)

                })
                .then((aWBProdTag: WB_FinishedProductTagRelationshipRow) => {
                    //
                    if (!(aWBProdTag)) {
                        error("Unable to create Event Tag record add to Product. ")
                        return
                    }
                    aWBProdTag.setDefaults()
                    aWBProdTag.WB_TagRow = aTagRow
                    aWBProdTag.cif.WB_TAG_ID = aTagRow.cif.WB_TAG_ID
                    aWBProdTag.cif.WB_PROD_ID = self.drWB_Product.cif.WB_PROD_ID
                    aWBProdTag.cif.TAG_METHOD = "Manual"
                    aWBProdTag.WB_TagRow = aTagRow
                    self.dbContext.add(esWB_Finished_Product_Tag_Relationship, aWBProdTag)
                    self.TagsChanged.next(self)
                    resolve()
                })
                .catch((err) => {
                    error(err)
                })
        })

    }

    removeTag(TagId: number): void {
        const self = this
        var changed = false
        _.forEach(this.drWB_Product.WB_FinishedProductTagRelationshipRows, (aWBProdTagRow: WB_FinishedProductTagRelationshipRow) => {
            if (aWBProdTagRow && aWBProdTagRow.isActive && aWBProdTagRow.WB_TagRow) {
                if (aWBProdTagRow.WB_TagRow.cif.WB_TAG_ID == TagId) {
                    aWBProdTagRow.markDeleted()
                    changed = true
                }
            }
        })

        if (changed) {
            self.TagsChanged.next(self)
        }
    }

    hasTag(TagId: number): boolean {
        var found = false

        _.forEach(this.drWB_Product.WB_FinishedProductTagRelationshipRows, (aWBProdTagRow: WB_FinishedProductTagRelationshipRow) => {
            if (aWBProdTagRow && aWBProdTagRow.isActive && aWBProdTagRow.WB_TagRow) {
                if (aWBProdTagRow.WB_TagRow.cif.WB_TAG_ID == TagId) {
                    found = true
                    return false // stop iterating
                }
            }
        })

        return found
    }

    setDateRange(TagId: number, DateRange: IDateRange): void {
        _.forEach(this.drWB_Product.WB_FinishedProductTagRelationshipRows, (aWBProdTagRow: WB_FinishedProductTagRelationshipRow) => {
            if (aWBProdTagRow && aWBProdTagRow.isActive && aWBProdTagRow.WB_TagRow) {
                if (aWBProdTagRow.WB_TagRow.cif.WB_TAG_ID == TagId) {
                    aWBProdTagRow.cif.EFFECTIVE_START_DT = DateRange.StartDt.getFullYear().toString() + ('0' + (DateRange.StartDt.getMonth() + 1)).slice(-2) + ('0' + DateRange.StartDt.getDate()).slice(-2)
                    aWBProdTagRow.cif.EFFECTIVE_END_DT = DateRange.EndDt.getFullYear().toString() + ('0' + (DateRange.EndDt.getMonth() + 1)).slice(-2) + ('0' + DateRange.EndDt.getDate()).slice(-2)
                    return
                }
            }
        })
    }

    getDateRange(TagId: number): IDateRange {
        let aDateRange: IDateRange = {}
        _.forEach(this.drWB_Product.WB_FinishedProductTagRelationshipRows, (aWBProdTagRow: WB_FinishedProductTagRelationshipRow) => {
            if (aWBProdTagRow && aWBProdTagRow.isActive && aWBProdTagRow.WB_TagRow) {
                if (aWBProdTagRow.WB_TagRow.cif.WB_TAG_ID == TagId) {
                    aDateRange.StartDt = new Date(Number(aWBProdTagRow.cif.EFFECTIVE_START_DT.slice(0, 4)), Number(aWBProdTagRow.cif.EFFECTIVE_START_DT.slice(4, 6)) - 1, Number(aWBProdTagRow.cif.EFFECTIVE_START_DT.slice(6, 8)))
                    aDateRange.EndDt = new Date(Number(aWBProdTagRow.cif.EFFECTIVE_END_DT.slice(0, 4)), Number(aWBProdTagRow.cif.EFFECTIVE_END_DT.slice(4, 6)) - 1, Number(aWBProdTagRow.cif.EFFECTIVE_END_DT.slice(6, 8)))

                    return aDateRange
                }
            }
        })

        return aDateRange
    }

//</editor-fold>

    //<editor-fold desc = "UOM operations">


    async addUOMRow(uomCd: string): Promise<WB_ProductUOMRow> {
        const self = this

        let newRow: WB_ProductUOMRow = <WB_ProductUOMRow>await self.dbContext.createRecordWithKey(esWB_Product_UOM, {})
        newRow.setDefaults()
        newRow.cif.WB_PROD_ID = self.WB_Product.cif.WB_PROD_ID
        newRow.WB_ProductRow = self.WB_Product
        newRow.cif.PRODUCT_UOM = uomCd
        self.dbContext.add(esWB_Product_UOM, newRow)
        return newRow
    }

    async removeUOMRow(wbPrdUOMId: number): Promise<void> {
        const self = this

        _.forEach(self.WB_Product.WB_ProductUOMRows, (a) => {
            if (!a.isActive)
                return
            if (a.cif.WB_PROD_UOM_ID == wbPrdUOMId) {
                a.markDeleted()
                return false
            }
        })
    }

    getUOMRow(uomCd: string, EffectiveDt?: Date): WB_ProductUOMRow {
        const self = this
        let bCheckDate = _.isDate(EffectiveDt)
        let sDt: string
        if (bCheckDate)
            sDt = DateUtils.convertToSAPBW(EffectiveDt)
        let evalRows = _.filter(self.WB_Product.WB_ProductUOMRows, (a) => a.isActive && (a.cif.PRODUCT_UOM == uomCd))
        evalRows = _.sortBy(evalRows, ['EFFECTIVE_END_DT', 'EFFECTIVE_START_DT'], ['desc', 'desc'])
        for (let aRow of evalRows) {

            if (!bCheckDate)
                return aRow

            if (sDt >= aRow.cif.EFFECTIVE_START_DT && sDt <= aRow.cif.EFFECTIVE_END_DT)
                return aRow

        }
        return undefined
    }


    // For Consumer Products, finds Shipment Products related to this Consumer Product
    async generateUOMs(pOptions: IUOMCalculateOptions): Promise<IMessageProvider> {
        const self = this
        //--
        //-- Check if this product a Consumer Product
        if (self.WB_Product.cif.PRODUCT_SET_ID.indexOf('UPC') < 0) {
            let mp = new MessageProvider()
            mp.addTextMessages('NotCnsPrd', MessageType.Information, ['UOM generation is only supported for Consumer Products'])
            return mp
        }

        let wbPrdId = self.WB_Product.cif.WB_PROD_ID

        // Get the Products in the named Hierarchy

        // Find the Finished Products related to me
        let arrFinishedPrds: WB_FinishedProductRow[]
            = <WB_FinishedProductRow[]>await self.dbContext.findAll(esWB_Finished_Product, {
            where: {
                WB_PROD_ID_CNSPRD_UPC: wbPrdId,
                DELETED_IND: {neq: '1'}
            }
        })

        if (pOptions.bExcludeDiscontinued) {
            // Filter finished Products

        }

        // Filter out any Consumer Product UPCs by Set Id
        arrFinishedPrds = _.filter(arrFinishedPrds, (a) => a.cif.PRODUCT_SET_ID.indexOf('UPC') < 0)
        let arrWbProdIds = _.map(arrFinishedPrds, (a) => a.cif.WB_PROD_ID)

        let options = _.cloneDeep(pOptions)
        options.WbProdIds = arrWbProdIds
        if (_.isEmpty(self.WB_Product.cif.BASE_UOM)) {
            self.WB_Product.cif.BASE_UOM = 'EA'
        }
        options.bDeriveUOM = false
        options.BaseUOM = self.WB_Product.cif.BASE_UOM

        let UOMEffDate = pOptions.EffectiveDate || DateUtils.today

        let logger = new AuditService()
        logger.setReference("Product", self.WB_Product.cif.WB_PROD_ID)
        options.AuditLogger = logger
        let results = await ProductUOMUtils.CalculateUOMs(options)
        await logger.Save()

        // Check if BaseUOM needs to be set
        if (options.bDeriveUOM)
            self.WB_Product.cif.BASE_UOM = options.BaseUOM

        // Sync Up the Conversion Factors that are Factors
        for (let aUOMCd in results.dctUOMs) {
            let conversionFactor = results.dctUOMs[aUOMCd]
            let row = self.getUOMRow(aUOMCd, UOMEffDate)
            if (row) {
                //-- Overwrites History, vs check if change creates new record
                row.cif.CONVERSION_FACTOR = conversionFactor
                continue
            } else {
                row = self.getUOMRow(aUOMCd)
                let sExistingEndDt
                if (row) {
                    // Doesn't overlap
                    sExistingEndDt = row.cif.EFFECTIVE_END_DT
                }
                //--
                let newRow = await self.addUOMRow(aUOMCd)
                newRow.setDefaults()
                newRow.cif.CONVERSION_FACTOR = conversionFactor
                newRow.cif.SOURCE_ID = 'WB'
                if (sExistingEndDt && sExistingEndDt != '99991231') {
                    // Change the Start Dt to EndDate + 1
                    //-- If needed, update this logic
                    let existingDt = DateUtils.convertFromSAPBW(sExistingEndDt)
                    if (_.isDate(existingDt)) {
                        existingDt = DateUtils.addDays(existingDt, 1)
                        newRow.cif.EFFECTIVE_START_DT = DateUtils.convertToSAPBW(existingDt)
                    }
                }
            }
        }

        return results.Messages
    }

    //</editor-fold>

    //<editor-fold desc = "Record Resolver operations">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {}
        let dbContext: PAdbContext

        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const prod_options: CreateOptions = {
            standardConditions: {
                [esWB_Finished_Product_Relationship]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_Product_BOM]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_Finished_Product_Tag_Relationship]: {"DELETED_IND": {"notIn": ['1']}},

            },
            dctIncludeBelongsToNamedRelations: {
                [esWB_SalesArea]: [esWB_SalesOrg, esWB_Division, esWB_DistribChannel]

            }
        }

        const aRecResolver = new ESRecordResolver(dbContext, prod_options)

        return aRecResolver;

    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_Finished_Product, ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

    //</editor-fold>
};

PAEnvironment.registerBusinessObjectClass(ProductBO, "ProductBO", "Product")